import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import RecipeCard from '../components/RecipeCard';
import SearchBar from '../components/SearchBar';
import { mockRecipes } from '../data/mockData';

const Feed = () => {
  const { user, isAuthenticated } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [filteredRecipes, setFilteredRecipes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilters, setSelectedFilters] = useState({
    dietary: [],
    cuisine: [],
    difficulty: ''
  });

  useEffect(() => {
    // Load recipes (in real app, this would be an API call)
    setRecipes(mockRecipes);
    setFilteredRecipes(mockRecipes);
  }, []);

  useEffect(() => {
    // Filter recipes based on search and filters
    let filtered = recipes;

    if (searchTerm) {
      filtered = filtered.filter(recipe =>
        recipe.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        recipe.ingredients.some(ingredient =>
          ingredient.toLowerCase().includes(searchTerm.toLowerCase())
        ) ||
        recipe.tags.some(tag =>
          tag.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    if (selectedFilters.dietary.length > 0) {
      filtered = filtered.filter(recipe =>
        selectedFilters.dietary.some(diet =>
          recipe.tags.includes(diet)
        )
      );
    }

    if (selectedFilters.cuisine.length > 0) {
      filtered = filtered.filter(recipe =>
        selectedFilters.cuisine.includes(recipe.cuisine)
      );
    }

    if (selectedFilters.difficulty) {
      filtered = filtered.filter(recipe =>
        recipe.difficulty === selectedFilters.difficulty
      );
    }

    setFilteredRecipes(filtered);
  }, [searchTerm, selectedFilters, recipes]);

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleFilterChange = (filterType, value) => {
    setSelectedFilters(prev => ({
      ...prev,
      [filterType]: Array.isArray(prev[filterType])
        ? prev[filterType].includes(value)
          ? prev[filterType].filter(item => item !== value)
          : [...prev[filterType], value]
        : value
    }));
  };

  if (!isAuthenticated) {
    return (
      <div className="page" style={{ display: 'grid', placeItems: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: 28, fontWeight: 800, marginBottom: 12 }}>Welcome to NutriLens Feed</h2>
          <p style={{ color: '#6b7280', marginBottom: 24 }}>Please log in to see personalized recipe recommendations</p>
          <a href="/login" className="btn btn-green">Log In</a>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="container">
        {/* Welcome Section */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={{ marginBottom: 24 }}>
          <h1 style={{ margin: 0 }}>Welcome back, {user?.username}!</h1>
          <p style={{ color: '#6b7280' }}>Discover new recipes tailored to your taste and nutritional goals</p>
        </motion.div>

        {/* Search and Filters */}
        <div style={{ marginBottom: 24 }}>
          <SearchBar onSearch={handleSearch} />

          {/* Filter Pills */}
          <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {['Vegetarian', 'Vegan', 'Gluten-Free', 'Keto', 'Low-Carb'].map(diet => {
              const active = selectedFilters.dietary.includes(diet);
              return (
                <button
                  key={diet}
                  onClick={() => handleFilterChange('dietary', diet)}
                  className="btn"
                  style={{
                    background: active ? 'var(--brand-600)' : '#f3f4f6',
                    color: active ? '#fff' : '#374151',
                    borderRadius: 999, padding: '8px 14px', fontWeight: 700,
                  }}
                >
                  {diet}
                </button>
              );
            })}
          </div>
        </div>

        {/* Recipe Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24 }}>
          {filteredRecipes.map((recipe, index) => (
            <motion.div key={recipe.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }}>
              <RecipeCard recipe={recipe} />
            </motion.div>
          ))}
        </div>

        {filteredRecipes.length === 0 && (
          <div className="text-center" style={{ padding: '48px 0' }}>
            <p style={{ color: '#6b7280', fontSize: 18 }}>No recipes found matching your criteria. Try adjusting your search or filters.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Feed;
